import React, { useEffect, useCallback } from 'react'
import { StateSchema, ValidationSchema, FormCallback } from './models'
import i18n from '../../i18n'

/**
 * @hook useForm
 * @description Custom Hook to handle Forms state, validation, and dependencies
 * @param {StateSchema} stateSchema
 * @param {ValidationSchema} validationSchema
 * @param {FormCallback} callback
 * @returns an Object containing the state, handleOnChange, handleOnChangeChecked, handleOnSubmit, disable props and default values
 */
function useForm(
  stateSchema: StateSchema,
  validationSchema: ValidationSchema,
  callback: FormCallback,
) {
  const [state, setState] = React.useState(stateSchema)
  const [disable, setDisable] = React.useState(true)
  const [isDirty, setIsDirty] = React.useState(false)

  const updateValue = useCallback((newState: StateSchema) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }))
  }, [])

  const setFormValues = React.useCallback(
    (newState: StateSchema) => {
      Object.keys(validationSchema).forEach((key) => {
        setState((prevState) => ({
          ...prevState,
          [key]: { value: newState[key]?.value, error: newState[key]?.error },
        }))
      })
    },
    [validationSchema],
  )

  const resetForm = React.useCallback(() => {
    Object.keys(validationSchema).forEach((key) => {
      setState((prevState) => ({
        ...prevState,
        [key]: { value: '', error: '' },
      }))
    })
  }, [validationSchema])

  useEffect(() => {
    setDisable(true)
  }, [])

  const validateState = React.useCallback(() => {
    const hasErrorInState = Object.keys(validationSchema).some((key) => {
      let isInputFieldRequired = validationSchema[key].required

      if (
        isInputFieldRequired &&
        validationSchema[key].dependencie !== undefined
      ) {
        const fieldName = validationSchema[key].dependencie?.fieldName || ''
        if (
          validationSchema[key].dependencie?.conditional?.(
            state[fieldName].value,
          )
        ) {
          isInputFieldRequired = true
        } else {
          isInputFieldRequired = false
        }
      }

      let stateValue = state[key] && state[key].value

      if (typeof state[key] === 'string') {
        stateValue =
          state[key] &&
          state[key].value &&
          state[key].value.toString().length > 0
      }

      const stateError = state[key] && state[key].error

      return (isInputFieldRequired && !stateValue) || stateError
    })
    return hasErrorInState
  }, [state, validationSchema])

  useEffect(() => {
    if (isDirty) {
      setDisable(validateState())
    }
  }, [state, isDirty, validateState])

  const handleOnChange = React.useCallback(
    (name: string, value: any) => {
      setIsDirty(true)

      let error = ''
      if (validationSchema[name] && validationSchema[name].required) {
        if (validationSchema[name].dependencie !== undefined) {
          const fieldName = validationSchema[name].dependencie?.fieldName || ''
          if (
            validationSchema[name].dependencie?.conditional?.(
              state[fieldName].value,
            )
          ) {
            if (!value || value.length === 0) {
              error = i18n.t('error.requiredField')
            }
          }
        } else if (!value || value.length === 0) {
          error = i18n.t('error.requiredField')
        }
      }

      if (
        validationSchema[name] &&
        validationSchema[name].validators !== null &&
        Array.isArray(validationSchema[name].validators)
      ) {
        validationSchema[name].validators.forEach((val, index) => {
          if (
            value &&
            validationSchema[name].validators[index].regExp !== undefined &&
            !validationSchema[name].validators[index].regExp?.test(
              typeof value === 'string'
                ? value.replace(/\r?\n|\r/g, ' ')
                : value,
            )
          ) {
            error = validationSchema[name].validators[index].error
          }

          if (
            value &&
            validationSchema[name].validators[index].regExpMatch !==
              undefined &&
            validationSchema[name].validators[index].regExpMatch?.test(value)
          ) {
            error = validationSchema[name].validators[index].error
          }

          if (
            value &&
            validationSchema[name].validators[index].conditional !==
              undefined &&
            !validationSchema[name].validators[index].conditional?.(
              value,
              state,
            )
          ) {
            error = validationSchema[name].validators[index].error
          }
        })
      }

      setState((prevState) => ({
        ...prevState,
        [name]: { value, error },
      }))
    },
    [validationSchema, state],
  )

  const handleOnChangeChecked = ({
    currentTarget: { checked, name },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true)

    let error = ''

    if (validationSchema[name] && validationSchema[name].required) {
      if (!checked) {
        error = i18n.t('error.requiredField')
      }
    }

    if (
      validationSchema[name] &&
      validationSchema[name].validators !== null &&
      Array.isArray(validationSchema[name].validators)
    ) {
      validationSchema[name].validators.forEach((val, index) => {
        if (
          checked &&
          validationSchema[name].validators[index].regExp !== undefined &&
          !validationSchema[name].validators[index].regExp?.test(
            checked ? 'true' : 'false',
          )
        ) {
          error = validationSchema[name].validators[index].error
        }
        if (
          checked &&
          validationSchema[name].validators[index].conditional !== undefined &&
          !validationSchema[name].validators[index].conditional?.(
            checked ? 'true' : 'false',
          )
        ) {
          error = validationSchema[name].validators[index].error
        }
      })
    }

    setState((prevState) => ({
      ...prevState,
      [name]: { value: checked, error },
    }))
  }

  const handleOnSubmit = React.useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault()
      if (!validateState()) {
        callback(state)
      }
    },
    [state, validateState, callback],
  )

  return {
    state,
    disable,
    isDirty,
    handleOnChange,
    handleOnChangeChecked,
    handleOnSubmit,
    resetForm,
    setFormValues,
    updateValue,
    setIsDirty,
    setDisable,
  }
}

export default useForm
